import React, { useState } from 'react';
import { Edit, Save, X, Check } from 'lucide-react';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const DisplayNameEditor = ({ skin, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(skin.display_name || skin.name || skin.skin_name || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Display name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const response = await adminAPI.updateSkinDisplayName(skin.id, displayName.trim());
      
      if (response.data.success) {
        toast.success('Display name updated successfully');
        setIsEditing(false);
        if (onUpdate) {
          onUpdate(skin.id, displayName.trim());
        }
      } else {
        toast.error(response.data.message || 'Failed to update display name');
      }
    } catch (error) {
      console.error('Error updating display name:', error);
      toast.error('Failed to update display name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(skin.display_name || skin.name || skin.skin_name || '');
    setIsEditing(false);
  };

  const currentDisplayName = skin.display_name || skin.name || skin.skin_name || 'Unnamed';
  const originalName = skin.name || skin.skin_name || '';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="flex-1 px-3 py-1 text-sm bg-gray-600 border border-gray-500 rounded text-white focus:outline-none focus:border-primary-400"
                placeholder="Enter display name..."
                disabled={saving}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSave();
                  } else if (e.key === 'Escape') {
                    handleCancel();
                  }
                }}
                autoFocus
              />
              <button
                onClick={handleSave}
                disabled={saving || !displayName.trim()}
                className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Save"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="p-1 text-gray-400 hover:text-gray-300 disabled:opacity-50"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-white">
                  {currentDisplayName}
                  {currentDisplayName !== originalName && (
                    <span className="ml-2 px-1 py-0.5 text-xs bg-blue-600 text-blue-100 rounded">
                      Custom
                    </span>
                  )}
                </div>
                {currentDisplayName !== originalName && originalName && (
                  <div className="text-xs text-gray-400 mt-1">
                    Original: {originalName}
                  </div>
                )}
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-gray-400 hover:text-white"
                title="Edit display name"
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisplayNameEditor;
